import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PropertyContextHeader from '../../components/ui/PropertyContextHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportPreview from './components/ReportPreview';
import ReportCustomization from './components/ReportCustomization';
import ReportDelivery from './components/ReportDelivery';
import { generateOfferAnalysis } from '../../services/aiService';
import { propertyService } from '../../services/propertyService';
import { offerService } from '../../services/offerService';
import { generateOfferComparisonPDF } from '../../services/pdfGeneratorService';
import { useAuth } from '../../contexts/AuthContext';

const ReportGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  // Get propertyId from state (priority) or query params (fallback)
  const searchParams = new URLSearchParams(location.search);
  const propertyId = location?.state?.propertyId || searchParams?.get('propertyId');

  const [property, setProperty] = useState(null);
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  const [includePropertyDetails, setIncludePropertyDetails] = useState(true);
  const [includeAgentNotes, setIncludeAgentNotes] = useState(false);
  
  // Check subscription tier for AI features
  const subscriptionTier = userProfile?.subscription_tier || 'free';
  const subscriptionStatus = userProfile?.subscription_status || 'inactive';
  const hasProAccess = subscriptionTier === 'pro' && ['active', 'trialing']?.includes(subscriptionStatus);
  
  const [aiEnabled, setAiEnabled] = useState(hasProAccess);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [agentNameDisplay, setAgentNameDisplay] = useState('full');
  const [buyerNameDisplay, setBuyerNameDisplay] = useState('full');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [contractUrls, setContractUrls] = useState({});

  const breadcrumbItems = [
  { label: 'Dashboard', path: '/agent-dashboard', icon: 'LayoutDashboard' },
  { label: 'Offer Management', path: '/offer-management', icon: 'FileText' },
  { label: 'Report Generation', icon: 'FileBarChart' }];


  const propertyActions = [
  {
    label: 'Back to Offers',
    icon: 'ArrowLeft',
    variant: 'outline',
    onClick: () => navigate(`/offer-management?propertyId=${propertyId}`)
  }];


  // Fetch property and offers data
  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId) {
        setLoadError('No property selected');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        // Fetch property data
        const { data: propertyData, error: propertyError } = await propertyService?.getProperty(propertyId);
        if (propertyError) throw new Error(propertyError);

        // Fetch property owners
        const ownerNames = propertyData?.property_owners?.map(o => o?.name)?.join(' & ') || 'No owner';
        const ownerEmails = propertyData?.property_owners?.map(o => o?.email) || [];

        // Transform property data
        const transformedProperty = {
          id: propertyData?.id,
          address: propertyData?.address,
          ownerNames,
          ownerEmails,
          listingPrice: propertyData?.listing_price,
          image: propertyData?.image_url,
          imageAlt: propertyData?.image_alt || 'Property image',
          status: propertyData?.status,
          commissionPercentage: propertyData?.commission_percentage || 0
        };

        setProperty(transformedProperty);

        // Fetch offers data
        const { data: offersData, error: offersError } = await offerService?.getOffers(propertyId);
        if (offersError) throw new Error(offersError);

        // Transform offers data to match component expectations
        const transformedOffers = offersData?.map(offer => ({
          id: offer?.id,
          buyerName: offer?.buyerName,
          buyerAgent: offer?.buyerAgent,
          offerPrice: offer?.offerPrice,
          downPayment: offer?.downPayment,
          financingType: offer?.financingType,
          contingencies: offer?.contingencies || [],
          closingDate: offer?.closingDate,
          earnestMoney: offer?.earnestMoney,
          lender: offer?.lender,
          agentNotes: offer?.internalNotes || '',
          preApprovalAmount: offer?.preApprovalAmount,
          buyersAgentCommission: offer?.buyersAgentCommission || 0
        }));

        setOffers(transformedOffers);

        // Pre-select all offers by default
        setSelectedOfferIds(transformedOffers?.map(o => o?.id));
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoadError(error?.message || 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [propertyId]);

  const handleOfferToggle = (offerId, checked) => {
    if (checked) {
      setSelectedOfferIds([...selectedOfferIds, offerId]);
    } else {
      setSelectedOfferIds(selectedOfferIds?.filter((id) => id !== offerId));
    }
  };

  const selectedOffers = offers?.filter((offer) => selectedOfferIds?.includes(offer?.id));
  const canGenerate = selectedOfferIds?.length > 0;

  // Generate AI analysis when AI is enabled and offers are selected
  React.useEffect(() => {
    const generateAiAnalysis = async () => {
      // Only generate AI analysis for Pro users
      if (aiEnabled && hasProAccess && selectedOffers?.length > 0 && property) {
        setIsLoadingAi(true);
        setAiError(null);
        try {
          const analysis = await generateOfferAnalysis(selectedOffers, property);
          setAiAnalysis(analysis);
        } catch (error) {
          setAiError(error?.message || 'Failed to generate AI analysis');
          setAiAnalysis(null);
        } finally {
          setIsLoadingAi(false);
        }
      } else {
        setAiAnalysis(null);
        setAiError(null);
      }
    };

    generateAiAnalysis();
  }, [aiEnabled, selectedOfferIds, offers, property, hasProAccess]);

  // Handle AI toggle - show upgrade modal for free users
  const handleAiToggle = (enabled) => {
    if (enabled && !hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setAiEnabled(enabled);
  };

  const handleDownload = () => {
    setIsGenerating(true);
    try {
      generateOfferComparisonPDF({
        property,
        selectedOffers,
        includePropertyDetails,
        includeAgentNotes,
        aiEnabled,
        aiAnalysis,
        agentNameDisplay,
        buyerNameDisplay,
        contractUrls
      });
      // Success - PDF will be downloaded automatically
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Failed to generate PDF: ${error?.message || 'Unknown error occurred'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/account?tab=subscription');
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">Loading property and offers...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (loadError || !property) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Icon name="AlertTriangle" size={48} className="text-warning mx-auto mb-4" />
              <p className="text-lg text-foreground mb-2">Unable to load property data</p>
              <p className="text-sm text-muted-foreground mb-4">{loadError || 'Property not found'}</p>
              <Button
                variant="outline"
                onClick={() => navigate('/agent-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs items={breadcrumbItems} />
      <PropertyContextHeader property={property} actions={propertyActions} />
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Generate Comparison Report
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Customize and generate a professional PDF comparison report for your sellers
              </p>
            </div>
            <Button
              variant="outline"
              size="default"
              onClick={() => setShowPreview(!showPreview)}
              iconName={showPreview ? 'EyeOff' : 'Eye'}
              iconPosition="left"
              className="lg:hidden">

              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Icon name="FileText" size={16} />
              <span>{offers?.length} Total Offers</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckSquare" size={16} />
              <span>{selectedOfferIds?.length} Selected</span>
            </div>
            {aiEnabled &&
            <div className="flex items-center gap-2 text-accent">
                <Icon name="Sparkles" size={16} />
                <span>AI Insights Enabled</span>
              </div>
            }
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="space-y-6 lg:col-span-1">
            <ReportCustomization
              offers={offers}
              selectedOfferIds={selectedOfferIds}
              onOfferToggle={handleOfferToggle}
              includePropertyDetails={includePropertyDetails}
              setIncludePropertyDetails={setIncludePropertyDetails}
              includeAgentNotes={includeAgentNotes}
              setIncludeAgentNotes={setIncludeAgentNotes}
              aiEnabled={aiEnabled}
              setAiEnabled={handleAiToggle}
              hasProAccess={hasProAccess}
              subscriptionTier={subscriptionTier}
              onUpgrade={handleUpgrade}
              agentNameDisplay={agentNameDisplay}
              setAgentNameDisplay={setAgentNameDisplay}
              agentNameDisplayChange={setAgentNameDisplay}
              buyerNameDisplay={buyerNameDisplay}
              setBuyerNameDisplay={setBuyerNameDisplay}
              buyerNameDisplayChange={setBuyerNameDisplay}
            />


            <ReportDelivery
              onDownload={handleDownload}
              isGenerating={isGenerating}
              canGenerate={canGenerate} />

          </div>

          <div className={`lg:col-span-2 ${showPreview ? 'block' : 'hidden lg:block'}`}>
            <div className="sticky top-24">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Eye" size={20} />
                  Report Preview
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(false)}
                  iconName="X"
                  className="lg:hidden">

                  Close
                </Button>
              </div>
              <ReportPreview
                property={property}
                selectedOffers={selectedOffers}
                includePropertyDetails={includePropertyDetails}
                includeAgentNotes={includeAgentNotes}
                aiEnabled={aiEnabled}
                aiAnalysis={aiAnalysis}
                isLoadingAi={isLoadingAi}
                aiError={aiError}
                agentNameDisplay={agentNameDisplay}
                buyerNameDisplay={buyerNameDisplay}
              />

            </div>
          </div>
        </div>
      </main>

      {/* Upgrade Modal for AI Features */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg shadow-elevation-2xl w-full max-w-md">
            <div className="p-6 md:p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Sparkles" size={32} className="text-primary" />
              </div>

              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-3">
                Upgrade for AI-Powered Insights
              </h2>

              <p className="text-sm md:text-base text-muted-foreground mb-2">
                AI-powered offer analysis and comparison features are available exclusively for Pro subscribers.
              </p>
              
              <p className="text-sm text-primary font-medium mb-6">
                Pro plans start at just $15/month or save 17% with $150/year.
              </p>

              <div className="bg-muted rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-semibold text-foreground mb-3">Pro Plan Benefits:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>Unlimited properties and offers</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>AI-powered offer summaries</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>Key difference highlighting</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>Professional PDF reports</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>Automatic email delivery</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setShowUpgradeModal(false)}
                  fullWidth
                >
                  Maybe Later
                </Button>
                <Button
                  variant="default"
                  onClick={() => navigate('/billing-dashboard')}
                  iconName="Crown"
                  iconPosition="left"
                  fullWidth
                >
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default ReportGeneration;